import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchLatestSha } from 'utils/services/fetchLatestSha';

// Same jittered cadence used by useMaintenanceConfig - see that file for why
// the jitter matters once this runs across many merchant domains at once.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const POLL_JITTER_MS = 60 * 1000;

/**
 * Polls /version.json and compares it against the sha baked into this bundle
 * (REACT_APP_CURRENT_GIT_SHA), so an open session can learn a new build has
 * shipped without needing a chunk to actually fail first. Triggers: mount, a
 * 5-minute (+jitter) interval, tab visibility, and every navigation - so a
 * shopper who's actively clicking around finds out sooner than the poll
 * interval alone would allow. See src/components/VersionWatcher and
 * docs/NETLIFY_MIGRATION.md, §9.
 *
 * Must be called within a Router (uses useLocation as the navigation
 * trigger) - true of every current call site (src/components/VersionWatcher,
 * mounted inside <Router> via src/context/AppContext).
 *
 * @returns {boolean} whether a newer/different build is available compared to the one running
 */
export function useAppVersion() {
  const [isStale, setIsStale] = useState(false);
  const currentSha = process.env.REACT_APP_CURRENT_GIT_SHA;
  const staleRef = useRef(false);
  const location = useLocation();
  const isFirstLocationRef = useRef(true);

  const check = useCallback(async () => {
    if (!currentSha || staleRef.current) return;
    const latestSha = await fetchLatestSha();
    if (latestSha && latestSha !== currentSha) {
      staleRef.current = true;
      setIsStale(true);
    }
  }, [currentSha]);

  // Mount + interval + tab-focus triggers, in their own effect so a
  // navigation (below) doesn't reset the poll timer or re-attach the
  // visibility listener on every route change.
  useEffect(() => {
    if (!currentSha) return undefined;
    let cancelled = false;
    const guardedCheck = () => {
      if (!cancelled) check();
    };

    guardedCheck();

    const interval = setInterval(
      guardedCheck,
      POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS,
    );

    function handleVisibility() {
      if (document.visibilityState === 'visible') guardedCheck();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentSha, check]);

  // Navigation trigger. Skips the very first location (already covered by
  // the mount check above) so a fresh page load doesn't fetch /version.json
  // twice.
  useEffect(() => {
    if (isFirstLocationRef.current) {
      isFirstLocationRef.current = false;
      return;
    }
    check();
  }, [location, check]);

  return isStale;
}
